import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { attentionDashboardsRestCrudThunksForSupplier } from '../../thunks/attention_dashboard_thunks';
import { Card, Row, Spin } from 'antd';
import EachWidget from '../attention_dashboard_components/EachWidget';
import ADHeader from '../attention_dashboard_components/ADHeader';
import ADAddWidgetPopup from '../attention_dashboard_components/ADAddWidgetPopup';
import { ROLE_TYPES } from '../../utils/DataConstants';
import AttentionDashboardEditPopup from '../attention_dashboard_components/AttentionDashboardEditPopup';
import OverlayContainer from '../common/OverlayContainer';
import { getObjectValues, nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { savedFilterViewsRestCrudThunksForSupplier } from '../../thunks/saved_filter_views_thunks';
import { kpiDates, kpis } from '../../constants/KPIs';
import DashboardKPICard from '../dashboard_KPI_card/DashboardKPICard';
import moment from 'moment';
import DashboardDateRangePicker from '../dashboard_date_range_picker/DashboardDateRangePicker';

const SupplierAttentionDashboardIndexPage = ({
	currentUser,
	attentionDashboard,
	getAttentionDashboard,
	saveAttentionDashboard,
	updateSavedFilterView,
	userType,
}): React.ReactElement => {
	const [dashboardViewsLoading, setDashboardViewsLoading] = useState(false);
	const [addWidgetModalVisible, setAddWidgetModalVisible] = useState(false);
	const [editWidgetModalVisible, setEditWidgetModalVisible] = useState(false);
	const [startDate, setStartDate] = useState(moment().subtract(1, 'months'));
	const [endDate, setEndDate] = useState(moment());

	const showAddWidgetModal = useCallback(() => setAddWidgetModalVisible(true), []);
	const hideAddWidgetModal = useCallback(() => setAddWidgetModalVisible(false), []);
	const showEditModal = useCallback(() => setEditWidgetModalVisible(true), []);
	const hideEditModal = useCallback(() => setEditWidgetModalVisible(false), []);

	const refreshDashboard = useCallback(() => {
		setDashboardViewsLoading(true);
		getAttentionDashboard().then(() => setDashboardViewsLoading(false));
	}, [getAttentionDashboard]);

	useEffect(() => {
		refreshDashboard();
	}, [refreshDashboard]);

	const onSaveDashboard = useCallback(
		(savedFilterViewIds) => {
			hideEditModal();
			setDashboardViewsLoading(true);
			saveAttentionDashboard({
				...attentionDashboard,
				savedFilterViewIds,
			}).then(() => setDashboardViewsLoading(false));
		},
		[hideEditModal, attentionDashboard, saveAttentionDashboard]
	);

	const timeAdjustedStartDate = startDate
		.clone()
		.add(startDate.utcOffset(), 'minutes')
		.startOf('day');
	const timeAdjustedEndDate = endDate.clone().add(endDate.utcOffset(), 'minutes').endOf('day');

	const KPIs = useMemo(() => kpis(currentUser, userType), [currentUser, userType]);
	const KPIdates = useMemo(
		() => kpiDates(currentUser, userType, timeAdjustedStartDate, timeAdjustedEndDate),
		[currentUser, userType, timeAdjustedStartDate, timeAdjustedEndDate]
	);

	return (
		<div
			style={{
				marginTop: '1em',
				width: '100%',
				position: 'relative',
			}}
		>
			<ADHeader
				addWidget={showAddWidgetModal}
				editDashboard={showEditModal}
				name={nullSafeGet('nameGiven', currentUser)}
			/>
			<Row style={{ display: 'flex', justifyContent: 'start' }}>
				<span
					className="truncate"
					style={{
						color: 'rgba(0, 0, 0, 0.85)',
						letterSpacing: '0.5px',
						fontSize: '17px',
						fontWeight: 400,
						marginLeft: 24,
					}}
				>
					Latest
				</span>
			</Row>
			<Row key={3} style={{ margin: '0.5em -8px' }} gutter={16}>
				{getObjectValues(KPIs).map((kpi, idx) => (
					<DashboardKPICard kpi={kpi} startDate={startDate} endDate={endDate} />
				))}
			</Row>
			,
			<DashboardDateRangePicker
				value={[startDate, endDate]}
				onChange={(d) => {
					if (d[0] && d[1]) {
						setStartDate(d[0]);
						setEndDate(d[1]);
					}
				}}
				disabledDate={(current) => current && current > moment()}
			/>
			<Row key={3} style={{ margin: '0.5em -8px' }} gutter={16}>
				{getObjectValues(KPIdates).map((kpi, idx) => (
					<DashboardKPICard
						kpi={kpi}
						startDate={startDate}
						endDate={endDate}
						showComparison={true}
					/>
				))}
			</Row>
			{dashboardViewsLoading ? (
				<OverlayContainer>
					<Spin />
				</OverlayContainer>
			) : (
				<div>
					<div>
						{attentionDashboard && attentionDashboard.savedFilterViews
							? attentionDashboard.savedFilterViews.map((savedView) => (
									<div className="rowSpacing" key={savedView.id}>
										<Card bodyStyle={{ padding: 0 }}>
											<EachWidget id={savedView.id} savedView={savedView} />
										</Card>
									</div>
							  ))
							: null}
					</div>
					{addWidgetModalVisible && (
						<ADAddWidgetPopup
							filterIds={attentionDashboard.savedFilterViewIds}
							roleType={ROLE_TYPES.SUPPLIER}
							onCancel={hideAddWidgetModal}
						/>
					)}
					{editWidgetModalVisible && (
						<AttentionDashboardEditPopup
							savedFilterViews={attentionDashboard.savedFilterViews}
							onCancel={hideEditModal}
							updateView={updateSavedFilterView}
							refreshDashboard={getAttentionDashboard}
							saveDashboard={onSaveDashboard}
						/>
					)}
				</div>
			)}
		</div>
	);
};

const mapStateToProps = (state) => ({
	currentUser: state.session.currentUser,
	attentionDashboard: state.attention_dashboards.detail,
});

const mapDispatchToProps = (dispatch) => ({
	getAttentionDashboard: () =>
		dispatch(attentionDashboardsRestCrudThunksForSupplier.readOne('default')),
	saveAttentionDashboard: (entity) =>
		dispatch(attentionDashboardsRestCrudThunksForSupplier.update(entity)),
	updateSavedFilterView: (entity) =>
		dispatch(savedFilterViewsRestCrudThunksForSupplier.update(entity)),
});
const ComponentWithoutUserType = withRouter(
	connect(mapStateToProps, mapDispatchToProps)(SupplierAttentionDashboardIndexPage)
);

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
	}),
	() => ({})
)(ComponentWithoutUserType);
